import { BaseSysCalls } from './BaseSysCalls';
import { Log, Registry, Router, Settings } from '@lightningjs/sdk';
import { setSysCalls, SysCalls } from './Syscalls';
import { TokenApi } from '../api/bootstrap/TokenApi';
import { Login } from '../Business/Login';
import { WebSysCalls } from './WebSysCalls';
import { Backend } from '../api/Backend';

declare let tizen: any | undefined;
declare let webapis: any | undefined;

export class TizenSysCalls extends BaseSysCalls {
  static override init() {
    if (!(window as any).tizen) {
      Log.error('[TizenSysCalls]', 'tizen library not available, falling back to dummy sys calls');
      setSysCalls(WebSysCalls);
      SysCalls.init();
      return;
    }
    this._initKeys();
    this._addHiddenEventListener();
    this._initAppCommonApi(() => {
      SysCalls.setScreensaver(true);
      this._addNetworkListeners();
    });
    super.addOnlineOfflineVideoHandlers();
    this._resumeVideo = false;
  }

  static _initAppCommonApi(onload: () => void) {
    const scriptId = 'tizenAppCommon';
    let appCommonScript: HTMLScriptElement | null = document.head.querySelector(`#${scriptId}`);
    if (appCommonScript) {
      return;
    }
    appCommonScript = document.createElement('script') as HTMLScriptElement;
    appCommonScript.id = scriptId;
    appCommonScript.type = 'text/javascript';
    appCommonScript.src = '$WEBAPIS/webapis/webapis.js';
    document.head.append(appCommonScript);
    appCommonScript.onload = onload;
  }

  static _initKeys() {
    if (!(window as any).tizen) return; // do not run if the tizen object is not available
    let i;
    const supportedKeys = Settings.get('app', 'tizenKeys', []);
    for (i = 0; i < supportedKeys.length; i++) {
      tizen.tvinputdevice.registerKey(supportedKeys[i]);
    }
  }

  static override exitApp() {
    tizen.application.getCurrentApplication().exit();
    Registry.removeEventListeners(document, 'visibilitychange');
  }

  static override setScreensaver(bool: boolean) {
    Log.info('setting screensaver to ', bool);
    const state = bool
      ? webapis.appcommon.AppCommonScreenSaverState.SCREEN_SAVER_ON
      : webapis.appcommon.AppCommonScreenSaverState.SCREEN_SAVER_OFF;
    webapis.appcommon.setScreenSaver(state);
  }

  static _addHiddenEventListener() {
    Registry.addEventListener(document, 'visibilitychange', async () => {
      if (document.hidden) {
        // close stream and send latest analytics
        if (this.backend.playerController.isStreaming) {
          this._resumeVideo = true;
          this.backend.playerController.endStream();
        }
        this.backend.logService.forceSendAnalytics();
      } else {
        try {
          await this.backend.reload();
        } catch (e) {
          Log.error('Initialization of backend failed', e);
          Router.navigate('fatalError');
          return;
        }
        // refresh token if needed
        const token = TokenApi.load();
        if (!token?.access?.isValid()) {
          if (token?.refresh && token?.refresh.isValid()) {
            const refreshedToken = await TokenApi.refresh(this.backend, token.refresh.token);
            if (refreshedToken?.access?.token && refreshedToken.access.isValid()) {
              this.backend.token = refreshedToken;
              TokenApi.save(refreshedToken);
              await Login.initSession(this.backend);
            }
          }
        }
        if (this._resumeVideo) {
          this._resumeVideo = false;
          this.backend.playerController.startRecentStream();
        }
      }
    });
  }

  static override _addNetworkListeners() {
    webapis.network.addNetworkStateChangeListener((value: unknown) => {
      if (value === webapis.network.NetworkState.GATEWAY_DISCONNECTED) {
        // Something you want to do when network is disconnected
        this.onOffline();
      } else if (value === webapis.network.NetworkState.GATEWAY_CONNECTED) {
        // Something you want to do when network is connected again
        this.onOnline();
      }
    });
  }

  static override platformVersion() {
    const version = tizen.systeminfo.getCapability('http://tizen.org/feature/platform.version') || 'unknown';
    return `Tizen ${version}`;
  }

  static override modelName() {
    return tizen.systeminfo.getCapability('http://tizen.org/system/model_name') || 'unknown';
  }

  static override cleanup(backend: Backend) {
    super.cleanup(backend);
    const scriptId = 'tizenAppCommon';
    const appCommonScript: HTMLScriptElement | null = document.head.querySelector(`#${scriptId}`);
    appCommonScript?.remove();
  }
}
